/* Shared quick-add for Liquid-rendered and dynamically rendered Shopify product cards.
 * The existing product-page slipcover consent dialog is reused verbatim.
 * No add action is allowed for a missing-slipcover variant without a designated image. */
(() => {
  'use strict';
  const price = cents => '$' + (Number(cents || 0) / 100).toLocaleString('en-CA', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  const singleFlight = new Map();
  let cartPromise = null;
  let latestCart = null;
  let latestAt = 0;
  let revision = 0;
  let publishedCart = null;
  const cartUrl = endpoint => (window.Shopify?.routes?.root || '/') + endpoint;
  function getCart(force = false) {
    if (!force && cartPromise) return cartPromise;
    if (!force && latestCart && Date.now() - latestAt < 10000) return Promise.resolve(latestCart);
    const stamp = ++revision;
    const request = fetch(cartUrl('cart.js'), {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store'})
      .then(response => { if (!response.ok) throw new Error('Unable to read the cart.'); return response.json(); })
      .then(cart => {
        if (stamp !== revision) return cartPromise || latestCart || getCart();
        latestCart = cart;
        latestAt = Date.now();
        return cart;
      })
      .finally(() => { if (cartPromise === request) cartPromise = null; });
    cartPromise = request;
    return request;
  }
  const selected = box => box.querySelector('[data-nubs-quick-condition]')?.selectedOptions[0] || null;
  const inCart = (id, cart = latestCart) => (cart?.items || []).filter(line => String(line.variant_id) === String(id)).reduce((total, line) => total + Number(line.quantity || 0), 0);
  function limit(option) {
    if (!option) return 0;
    if (option.dataset.nubsAvailable !== 'true') return 0;
    if (option.dataset.nubsManagement && option.dataset.nubsPolicy !== 'continue') {
      return Math.max(0, Number(option.dataset.nubsStock || 0) - inCart(option.value));
    }
    return 999;
  }
  function cardImage(box, option) {
    const card = box.parentElement;
    const image = card?.querySelector('.product-card__cover img, .nubs-collection-card__image, .nubs-search-card__image, .nubs-related__image, .nubs-recently-viewed__image');
    if (!image) return;
    if (!image.dataset.nubsDefaultSrc) {
      image.dataset.nubsDefaultSrc = image.getAttribute('src') || '';
      image.dataset.nubsDefaultSrcset = image.getAttribute('srcset') || '';
    }
    const missing = option.dataset.nubsMissing === 'true';
    const variantImage = option.dataset.nubsImage || '';
    // For a missing-slipcover copy, NEVER fall back to artwork showing a slipcover.
    const next = missing ? variantImage : (variantImage || box.dataset.nubsDefaultProductImage || image.dataset.nubsDefaultSrc);
    if (!next) return;
    if (image.getAttribute('src') !== next) {
      image.src = next;
      image.removeAttribute('srcset');
    }
    if (!missing && !variantImage && !box.dataset.nubsDefaultProductImage && image.dataset.nubsDefaultSrcset) {
      image.setAttribute('srcset', image.dataset.nubsDefaultSrcset);
    }
  }
  function update(box) {
    if (!box.isConnected) return;
    const option = selected(box);
    if (!option) return;
    const amount = box.querySelector('[data-nubs-quick-quantity]');
    const button = box.querySelector('[data-nubs-quick-submit]');
    const minus = box.querySelector('[data-nubs-quick-minus]');
    const plus = box.querySelector('[data-nubs-quick-plus]');
    const remove = box.querySelector('[data-nubs-quick-remove]');
    const note = box.querySelector('[data-nubs-quick-note]');
    if (!amount || !button) return;
    const missing = option.dataset.nubsMissing === 'true';
    const imageMissing = missing && !option.dataset.nubsImage;
    const cartCount = inCart(option.value);
    const already = cartCount > 0;
    const max = imageMissing ? 0 : (already && option.dataset.nubsManagement && option.dataset.nubsPolicy !== 'continue'
      ? Math.max(cartCount, Number(option.dataset.nubsStock || 0)) : limit(option));
    const differentVariant = box.dataset.nubsControlVariant !== option.value;
    const wasCartControlled = box.dataset.nubsCartControlled === '1';
    if (differentVariant || wasCartControlled !== already || box.dataset.nubsSyncRequested === '1') amount.value = String(already ? cartCount : 1);
    delete box.dataset.nubsSyncRequested;
    box.dataset.nubsControlVariant = option.value;
    box.dataset.nubsCartControlled = already ? '1' : '0';
    const validQty = already ? (box.dataset.nubsPending ? cartCount : Math.max(0, Number.parseInt(amount.value, 10) || 0)) : Math.min(Math.max(1, Number.parseInt(amount.value, 10) || 1), Math.max(1, max));
    amount.value = String(validQty);
    amount.max = String(Math.max(1, max));
    amount.min = already ? '0' : '1';
    amount.disabled = max === 0 || Boolean(box.dataset.nubsPending);
    if (minus) minus.disabled = Boolean(box.dataset.nubsPending) || (already ? false : validQty <= 1 || max === 0);
    if (plus) plus.disabled = Boolean(box.dataset.nubsPending) || validQty >= max;
    button.disabled = Boolean(box.dataset.nubsPending) || (!already && max === 0);
    button.textContent = box.dataset.nubsPending ? 'Updating…' : already ? 'View in Cart' : imageMissing ? 'Cover photo unavailable' : !max ? 'Sold out' : 'Add to Cart';
    const actions = box.querySelector('.nubs-quick-add__actions');
    if (actions) {
      if (already) actions.setAttribute('data-nubs-in-cart', '');
      else actions.removeAttribute('data-nubs-in-cart');
    }
    // Reserve the removal link's row even for items not in the cart, so
    // purchasing controls in adjacent product cards stay aligned.
    if (remove) {
      remove.hidden = false;
      remove.classList.toggle('nubs-quick-add__remove--inactive', !already);
      remove.disabled = !already || Boolean(box.dataset.nubsPending);
      remove.setAttribute('aria-hidden', already ? 'false' : 'true');
      remove.tabIndex = already ? 0 : -1;
    }
    if (note) {
      const detail = option.dataset.nubsNotes?.trim();
      note.textContent = missing ? 'Missing slipcover: the original slipcover is not included.' : (detail || '');
      note.hidden = !note.textContent;
    }
    const card = box.parentElement;
    const link = card?.querySelector('a.product-card__link, a.nubs-collection-card__link, a.nubs-search-card__link, a.nubs-related__link, a.nubs-recently-viewed__item');
    if (link) {
      const url = new URL(link.href);
      url.searchParams.set('variant', option.value);
      link.href = url.toString();
    }
    const priceElement = card?.querySelector('.product-card__current-price, .nubs-collection-card__current-price, .nubs-search-card__current-price, .nubs-related__current-price, .nubs-recently-viewed__price > span');
    if (priceElement) {
      if (!priceElement.dataset.nubsInitialPrice) priceElement.dataset.nubsInitialPrice = priceElement.textContent.trim();
      priceElement.textContent = price(option.dataset.nubsPrice);
      // The reference prices belong to the initially displayed condition. Avoid
      // displaying inaccurate MSRP/savings for a different condition.
      const selectedInitial = box.dataset.nubsInitialVariant === option.value;
      card.querySelectorAll('.product-card__compare-price, .nubs-collection-card__compare-price, .nubs-search-card__compare-price, .nubs-related__compare-price, .nubs-recently-viewed__compare-price, .product-card__savings, .nubs-collection-card__savings, .nubs-search-card__savings, .nubs-related__savings').forEach(node => { node.hidden = !selectedInitial; });
    }
    // Liquid cards already render their exact chosen cover; avoid replacing it
    // during first paint. Remote cards need their correct variant cover after fetch.
    if (box.hasAttribute('data-nubs-quick-remote') || box.dataset.nubsRenderedVariant) cardImage(box, option);
    box.dataset.nubsRenderedVariant = option.value;
  }
  let feedbackKeyListener = null;
  function showFeedback(cart, productTitle,variantId=null,kind='added') {
    const feedback = document.querySelector('[data-nubs-cart-feedback]');
    if (!feedback) return;
    const product = feedback.querySelector('[data-nubs-cart-feedback-product]');
    const threshold = feedback.querySelector('[data-nubs-cart-feedback-threshold]');
    const progress = feedback.querySelector('[data-nubs-cart-feedback-progress]');
    const fill = feedback.querySelector('[data-nubs-cart-feedback-progress-fill]');
    const description = feedback.querySelector('[data-nubs-cart-feedback-description]');
    const importNote = feedback.querySelector('[data-nubs-cart-feedback-import-note]');
    const us = document.documentElement.dataset.nubsShippingCountry === 'US';
    const target = us ? 15000 : 10000;
    const total = Number(us ? cart?.total_price : (cart?.items_subtotal_price ?? cart?.total_price) || 0);
    const remaining = Math.max(0, target - total);
    const percent = Math.min(100, Math.round(total * 100 / target));
    if (product) product.textContent = productTitle;
    const title = feedback.querySelector('.nubs-cart-feedback__title');
    if (title) title.textContent = kind === 'added' ? 'Added to cart!' : kind === 'edit' ? 'Your cart' : 'Cart updated!';
    if (threshold) threshold.textContent = remaining === 0
      ? (us ? 'Your order has reached the $150 CAD minimum for free U.S. Economy Tracked Shipping!' : 'Your order has reached the $100 CAD spending minimum for free Canadian Budget Tracked Shipping.')
      : `${price(remaining)} away from ${us ? 'free U.S. Economy Tracked Shipping at $150 CAD!' : 'the $100 CAD minimum for free Canadian Budget Tracked Shipping!'}`;
    if (progress) progress.setAttribute('aria-valuenow', String(percent));
    if (fill) {
      if (fill.dataset.nubsProgressClass) fill.classList.remove(fill.dataset.nubsProgressClass);
      const cls = `nubs-progress-p${percent}`;
      fill.dataset.nubsProgressClass = cls;
      fill.classList.add(cls);
    }
    if (description) description.textContent = us
      ? 'United States: Economy shipping costs $8.99 CAD below $150 CAD at eligible destinations. Confirmed at checkout.'
      : 'Canada: The $100 CAD minimum is measured before whole-order discounts. Checkout confirms whether Budget Tracked Shipping qualifies for free shipping at your address.';
    if (importNote) importNote.hidden = !us;
    feedback.hidden = false;
    document.dispatchEvent(new CustomEvent('nubs:feedback-open', {detail:{cart,variantId}}));
    feedback.querySelectorAll('[data-nubs-cart-feedback-close]').forEach(button => { button.onclick = () => { feedback.hidden = true; }; });
    if (feedbackKeyListener) document.removeEventListener('keydown', feedbackKeyListener);
    feedbackKeyListener = event => { if (event.key === 'Escape') { feedback.hidden = true; document.removeEventListener('keydown', feedbackKeyListener); feedbackKeyListener = null; } };
    document.addEventListener('keydown', feedbackKeyListener);
  }
  function makeControl(tag, cls, content, attr) {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (attr) el.setAttribute(attr, '');
    if (content) el.textContent = content;
    return el;
  }
  function buildRemote(box, product) {
    if (!box.isConnected) return;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return;
    const heading = makeControl('div', 'nubs-quick-add__condition-row');
    heading.append(makeControl('span', '', 'Condition'));
    const help = makeControl('details', 'nubs-quick-add__help');
    const summary = makeControl('summary', '', '?');
    summary.title = 'Condition guide'; summary.setAttribute('aria-label', 'What do the conditions mean?');
    const panel = makeControl('div', 'nubs-quick-add__help-panel');
    const strong = makeControl('strong', '', 'Item Condition Guide');
    const guide = makeControl('p', '', 'New: unopened. Used - Very Good: inspected used copy. Missing Slipcover: no original slipcover included. Other slipcovers are included only if shown on the main product image.');
    panel.append(strong, guide); help.append(summary, panel); heading.append(help);
    const label = makeControl('label', 'nubs-quick-add__select-wrap');
    const labelText = makeControl('span', 'visually-hidden', 'Choose condition');
    const select = makeControl('select', 'nubs-quick-add__select', '', 'data-nubs-quick-condition');
    let preferred = box.dataset.nubsPreferredVariant;
    if (!variants.some(v => String(v.id) === preferred && v.available)) {
      preferred = String(window.NubsConditions?.preferred(product)?.id || variants.find(v => v.available)?.id || variants[0].id);
    }
    for (const variant of variants) {
      const missing = Boolean(window.NubsConditions?.missing(variant)) || /(?:missing|without|no)\s*slip\s*cover/i.test(variant.title || '');
      const opt = document.createElement('option');
      opt.value = String(variant.id);
      opt.textContent = (variant.title === 'Default Title' ? 'New' : variant.title === 'Used - Missing Slipcover' ? 'Used - Very Good (Missing Slipcover)' : variant.title) + (variant.available ? '' : ' - Sold out');
      opt.dataset.nubsTitle = variant.title;
      opt.dataset.nubsPrice = String(variant.price ?? 0);
      opt.dataset.nubsAvailable = String(Boolean(variant.available));
      opt.dataset.nubsManagement = (variant.inventory_quantity === undefined || variant.inventory_quantity === null) ? '' : (variant.inventory_management || '');
      opt.dataset.nubsPolicy = variant.inventory_policy || '';
      opt.dataset.nubsStock = String(variant.inventory_quantity ?? 0);
      opt.dataset.nubsMissing = String(missing);
      opt.dataset.nubsImage = variant.featured_image?.src || variant.featured_image?.url || '';
      // Do not use the product cover as a substitute for the designated image.
      opt.dataset.nubsImageId = String(variant.featured_image?.id || 0);
      if (String(variant.id) === preferred) opt.selected = true;
      select.append(opt);
    }
    label.append(labelText, select);
    const note = makeControl('p', 'nubs-quick-add__note', '', 'data-nubs-quick-note'); note.hidden = true;
    const actions = makeControl('div', 'nubs-quick-add__actions');
    const qty = makeControl('div', 'nubs-quick-add__quantity'); qty.setAttribute('aria-label', 'Quantity');
    const minus = makeControl('button', '', '−', 'data-nubs-quick-minus'); minus.type = 'button'; minus.setAttribute('aria-label', 'Decrease quantity');
    const input = makeControl('input', '', '', 'data-nubs-quick-quantity'); input.type = 'number'; input.min = '1'; input.step = '1'; input.value = '1'; input.inputMode = 'numeric'; input.setAttribute('aria-label', 'Quantity');
    const plus = makeControl('button', '', '+', 'data-nubs-quick-plus'); plus.type = 'button'; plus.setAttribute('aria-label', 'Increase quantity');
    const add = makeControl('button', 'nubs-quick-add__button', 'Add to Cart', 'data-nubs-quick-submit'); add.type = 'button';
    qty.append(minus, input, plus); actions.append(qty, add);
    const error = makeControl('p', 'nubs-quick-add__error', '', 'data-nubs-quick-error'); error.hidden = true; error.setAttribute('role', 'alert');
    const remove = makeControl('button', 'nubs-quick-add__remove', 'Remove from cart', 'data-nubs-quick-remove'); remove.type = 'button'; remove.hidden = true;
    box.append(heading, label, note, actions, remove, error);
    box.dataset.nubsInitialVariant = preferred;
    box.dataset.nubsDefaultProductImage = typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image?.src || product.image || '');
    update(box);
  }
  async function loadRemote(box) {
    if (!box.isConnected || box.dataset.nubsRemoteLoaded) return;
    box.dataset.nubsRemoteLoaded = '1';
    const handle = box.dataset.nubsProductHandle;
    if (!handle || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return;
    try {
      const key = handle;
      if (!singleFlight.has(key)) singleFlight.set(key, fetch(`/products/${encodeURIComponent(handle)}.js`, {credentials: 'same-origin'})
        .then(response => { if (!response.ok) throw new Error('Product unavailable.'); return response.json(); })
        .finally(() => singleFlight.delete(key)));
      const product = await singleFlight.get(key);
      buildRemote(box, product);
    } catch (error) {
      if (box.isConnected) box.textContent = '';
      console.debug('Quick-add unavailable for card:', handle, error);
    }
  }
  const remoteObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { remoteObserver.unobserve(entry.target); loadRemote(entry.target); }
      }), {rootMargin: '180px'}) : null;
  function scan(root = document) {
    const list = [];
    if (root.matches?.('[data-nubs-quick-add]')) list.push(root);
    root.querySelectorAll?.('[data-nubs-quick-add]').forEach(box => list.push(box));
    list.forEach(box => {
      if (box.dataset.nubsQuickReady) return;
      box.dataset.nubsQuickReady = '1';
      if (box.hasAttribute('data-nubs-quick-remote')) {
        if (remoteObserver) remoteObserver.observe(box);
        else loadRemote(box);
      } else {
        box.dataset.nubsInitialVariant = selected(box)?.value || '';
        update(box);
      }
    });
  }
  // A top-layer modal avoids condition help being clipped by a carousel viewport.
  // The native <details> remains a usable fallback if <dialog> is unavailable.
  document.addEventListener('click', event => {
    const summary = event.target.closest?.('.nubs-quick-add__help > summary');
    if (!summary || typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    const guide = summary.closest('.nubs-quick-add__help')?.querySelector('.nubs-quick-add__help-panel');
    if (!guide) return;
    event.preventDefault();
    const modal = document.createElement('dialog');
    modal.className = 'nubs-quick-add__guide-modal';
    modal.setAttribute('aria-label', 'Item Condition Guide');
    const content = guide.cloneNode(true);
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'nubs-quick-add__guide-close';
    dismiss.textContent = 'Close';
    dismiss.setAttribute('aria-label', 'Close condition guide');
    modal.append(content, dismiss);
    document.body.append(modal);
    const finish = () => { modal.remove(); if (summary.isConnected) summary.focus(); };
    dismiss.addEventListener('click', () => modal.close());
    modal.addEventListener('close', finish, {once:true});
    modal.addEventListener('click', click => { if (click.target === modal) modal.close(); });
    try { modal.showModal(); dismiss.focus(); }
    catch (_) { modal.remove(); }
  });
  document.addEventListener('change', event => {
    const box = event.target.closest?.('[data-nubs-quick-add]');
    if (box && event.target.matches('[data-nubs-quick-condition]')) update(box);
  });
  const cartLines = (cart, variantId) => (cart?.items || []).filter(line => String(line.variant_id) === String(variantId));
  function syncCart(cart) {
    if (publishedCart === cart) return;
    publishedCart = cart;
    document.dispatchEvent(new CustomEvent('nubs:cart-changed', {detail:{cart}}));
    document.querySelectorAll('[data-site-cart-count]').forEach(badge => {
      badge.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
      badge.hidden = !cart.item_count;
    });
    const cartLink = document.querySelector('.site-cart');
    if (cartLink) cartLink.setAttribute('aria-label', `Cart, ${cart.item_count} items`);
  }
  async function requestCartChange(lineKey, quantity) {
    const response = await fetch(cartUrl('cart/change.js'), {
      method:'POST', credentials:'same-origin',
      headers:{'Content-Type':'application/json',Accept:'application/json'},
      body:JSON.stringify({id:lineKey,quantity})
    });
    if (!response.ok) {
      let detail;
      try {detail=await response.json();} catch(_){}
      throw new Error(detail?.description || detail?.message || 'Unable to update your cart.');
    }
    const cart = await response.json();
    syncCart(cart);
    return cart;
  }
  async function requestCartAdd(variantId, quantity, properties = null) {
    const data = {id:Number(variantId),quantity};
    if (properties && Object.keys(properties).length) data.properties=properties;
    const response = await fetch(cartUrl('cart/add.js'), {
      method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(data)
    });
    if (!response.ok) {
      let detail;
      try {detail=await response.json();} catch(_){}
      throw new Error(detail?.description || detail?.message || 'Unable to add this condition to your cart.');
    }
    await response.json();
    const cart = await getCart(true);
    syncCart(cart);
    return cart;
  }
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-nubs-quick-minus], [data-nubs-quick-plus], [data-nubs-quick-submit], [data-nubs-quick-remove]');
    if (!button) return;
    const box = button.closest('[data-nubs-quick-add]');
    if (!box || box.dataset.nubsPending) return;
    event.preventDefault(); event.stopPropagation();
    const input = box.querySelector('[data-nubs-quick-quantity]');
    const option = selected(box);
    if (!input || !option) return;
    const errorNode = box.querySelector('[data-nubs-quick-error]');
    if (errorNode) errorNode.hidden = true;
    try {
      await getCart();
      if (!box.isConnected || selected(box) !== option) return;
      const lines = cartLines(latestCart, option.value);
      const already = lines.reduce((n,line) => n + Number(line.quantity || 0),0);
      if (button.matches('[data-nubs-quick-submit]') && already) {
        // Viewing the cart must be a real navigation, not an inert popover.
        window.location.assign(window.Shopify?.routes?.root ? `${window.Shopify.routes.root.replace(/\/$/, '')}/cart` : '/cart');
        return;
      }
      const isRemove = button.matches('[data-nubs-quick-remove]');
      const isMinus = button.matches('[data-nubs-quick-minus]');
      const isPlus = button.matches('[data-nubs-quick-plus]');
      if (isRemove && !already) return;
      if (already && (isRemove || isMinus || isPlus)) {
        // Shopify cart/change.js addresses exact line keys. Respect separate
        // custom-property lines instead of mistakenly editing another variant.
        const line = lines[0];
        if (!line) return;
        if (!isRemove && lines.length > 1) throw new Error('This condition has multiple separate cart lines. Adjust them in the cart page.');
        const newQty = isRemove ? 0 : Math.max(0, Number(line.quantity) + (isPlus ? 1 : -1));
        const maximum = option.dataset.nubsManagement && option.dataset.nubsPolicy !== 'continue'
          ? Number(option.dataset.nubsStock || 0) : 999;
        if (newQty > maximum) return;
        box.dataset.nubsPending='1'; update(box);
        let cart;
        if (isRemove) {
          // Variant can occupy multiple distinct lines when line properties differ.
          // Remove every matching line, without affecting other variants.
          // Refresh after each mutation: Shopify can recompute line keys.
          for (let attempts=0; attempts<30; attempts++) {
            const current = (latestCart?.items || []).find(item => String(item.variant_id) === String(option.value));
            if (!current) break;
            cart = await requestCartChange(current.key,0);
          }
          if ((latestCart?.items || []).some(item=>String(item.variant_id)===String(option.value)))
            throw new Error('Some copies could not be removed. Please review your cart.');
          cart ||= latestCart;
        } else cart = await requestCartChange(line.key, newQty);
        if (box.isConnected) showFeedback(cart, box.dataset.nubsProductTitle || 'Cart updated', option.value, 'updated');
        return;
      }
      if (isMinus || isPlus) {
        const delta = isPlus ? 1 : -1;
        input.value = String(Math.max(1, Math.min(limit(option), Number(input.value || 1) + delta)));
        update(box);
        return;
      }
      if (!button.matches('[data-nubs-quick-submit]')) return;
      if (option.dataset.nubsAvailable !== 'true') {update(box); return;}
      const missing = option.dataset.nubsMissing === 'true';
      if (missing && (!option.dataset.nubsImage || !Number(option.dataset.nubsImageId)))
        throw new Error('Designated missing-slipcover image unavailable. Please open the product page.');
      const count = Math.min(Math.max(1, Number.parseInt(input.value,10) || 1),limit(option));
      if (!count) {update(box);return;}
      if (missing) {
        if (!window.NubsConditions?.confirmMissing) throw new Error('Please reload to confirm the missing-slipcover condition.');
        const accepted = await window.NubsConditions.confirmMissing({id:option.value,title:option.dataset.nubsTitle,missingSlipcover:true,featuredImageId:Number(option.dataset.nubsImageId)},document.body,button);
        if (!accepted || !box.isConnected || selected(box) !== option) return;
      }
      box.dataset.nubsPending='1'; update(box);
      const cart = await requestCartAdd(option.value,count);
      if (box.isConnected) showFeedback(cart, `${box.dataset.nubsProductTitle || 'Item'} (${option.dataset.nubsTitle || 'Selected condition'})`,option.value,'added');
    } catch(error) {
      if (box.isConnected && errorNode) {errorNode.textContent=error.message || 'Unable to update cart.';errorNode.hidden=false;}
      if (typeof window.displayNotification === 'function') window.displayNotification(error.message || 'Unable to update cart.','error',false);
    } finally {
      delete box.dataset.nubsPending;
      if (box.isConnected) update(box);
    }
  });
  document.addEventListener('change', async event => {
    const input = event.target.closest?.('[data-nubs-quick-quantity]');
    if (!input) return;
    const box = input.closest('[data-nubs-quick-add]');
    if (!box || box.dataset.nubsPending) return;
    const option = selected(box); if (!option) return;
    try {
      await getCart();
      const lines = cartLines(latestCart,option.value);
      if (!lines.length) {update(box);return;}
      if (lines.length>1) throw new Error('This condition has multiple cart lines. Adjust them in the cart page.');
      const desired = Math.max(0,Math.round(Number(input.value) || 0));
      const maximum = option.dataset.nubsManagement && option.dataset.nubsPolicy !== 'continue'
        ? Number(option.dataset.nubsStock || 0) : 999;
      if (desired > maximum) throw new Error('Requested quantity exceeds available stock.');
      box.dataset.nubsPending='1';update(box);
      const cart = await requestCartChange(lines[0].key,desired);
      showFeedback(cart,box.dataset.nubsProductTitle || 'Cart updated',option.value,'updated');
    } catch(error) {
      const node=box.querySelector('[data-nubs-quick-error]');
      if(node){node.textContent=error.message;node.hidden=false;}
    } finally {delete box.dataset.nubsPending;update(box);}
  });
  // Keep card quantities in sync when the product page opens the feedback
  // panel. The popup itself is informational: the cards and /cart handle edits.
  document.addEventListener('nubs:feedback-open', event => {
    if (event.detail?.cart) syncCart(event.detail.cart);
    else getCart(true).then(syncCart).catch(() => {});
  });
  window.NubsQuickCart = {showFeedback,requestCartChange,getCart,syncCart};

  document.addEventListener('nubs:cart-changed', event => {
    revision++;
    cartPromise = null;
    latestCart = event.detail?.cart || null;
    publishedCart = latestCart;
    latestAt = Date.now();
    if (latestCart) document.querySelectorAll('[data-nubs-quick-add]').forEach(box => {
      box.dataset.nubsSyncRequested = '1';
      update(box);
    });
  });
  // One inexpensive cart-state read for reliable View in Cart and stock caps.
  function init() {
    scan();
    getCart().then(() => document.querySelectorAll('[data-nubs-quick-add]').forEach(update)).catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  const observer = new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
  });
  observer.observe(document.body || document.documentElement, {subtree: true, childList: true});
  document.addEventListener('nubs:pagechange', () => { scan(); getCart().then(() => document.querySelectorAll('[data-nubs-quick-add]').forEach(update)).catch(() => {}); });
})();
